import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { useFormikContext } from 'formik'
import * as React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import { SortButtons } from 'src/views/components/SortButtons'
import SemesterSelect from 'src/views/includes/SemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { AdministratorCoursesHeaderActions } from './AdministratorCoursesHeaderActions'

export default function AdministratorCoursesPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('course:learning_courses')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    semId: '',
    status: '',
    degree: '',
    facId: '',
    type: '',
    query: '',
    sortBy: '',
  })
  const t = useTranslatable()
  const locale = useLocale()
  const activeSemester = useActiveSemester()
  React.useEffect(() => {
    const semFromUrl = searchParams.get('semId')
    if (semFromUrl != null && semFromUrl !== '') {
      return
    }
    if (activeSemester?.id == null) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('semId', String(activeSemester.id))
        return next
      },
      { replace: true }
    )
  }, [searchParams, activeSemester?.id, setSearchParams])

  const query = searchParams.get('query') || ''
  const semId = searchParams.get('semId') || ''
  const status = searchParams.get('status') || ''
  const degree = searchParams.get('degree') || ''
  const facId = searchParams.get('facId') || ''
  const typeParam = searchParams.get('type') || ''
  const general = searchParams.get('general') === 'true'
  const initialValues: FormikValues = { query, semId, status, degree, facId, type: typeParam, general }

  const breadcrumbsItems = [
    {
      page: t('course:learning_courses'),
      path: `/${locale}/administrator/administration/courses`,
    },
  ]

  const handleFormSubmit = React.useCallback(
    (values: FormikValues) => {
      setSearchParams((params) => {
        Object.entries(values).forEach(([key, value]) => {
          if (key === 'general') {
            if (value === true) {
              params.set(key, 'true')
            } else {
              params.delete(key)
            }
            return
          }

          if (value) {
            params.set(key, value)
          } else {
            params.delete(key)
          }
        })
        params.set('page', DEFAULT_PAGE)
        return params
      })
    },
    [setSearchParams]
  )

  return (
    <Section
      title={t('course:learning_courses')}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faBook} />}
      breadcrubms={breadcrumbsItems}
      rightElement={<AdministratorCoursesHeaderActions />}
    >
      <Form
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch />
      </Form>

      <SuspenseWrapper>
        <CoursesTableContent searchParams={searchParams} />
      </SuspenseWrapper>
    </Section>
  )
}

const DEFAULT_PAGE = '1'
const DEFAULT_PER_PAGE = '10'

interface FormikValues {
  readonly query: string
  readonly semId: string
  readonly status: string
  readonly degree: string
  readonly facId: string
  readonly type: string
  readonly general: boolean
}

function FormSearch(): React.ReactElement {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const activeSemester = useActiveSemester()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: faculties } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const { data: degreesResponse } = useApi({
    endpoint: Api.getAdministratorListsDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const degrees = degreesResponse

  const { data: courseDefinitions } = useApi({
    endpoint: Api.getAdministratorCourseDefinitions,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const { data: creditTypesResponse } = useApi({
    endpoint: Api.getAdministratorListsCreditTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const currentQuery = searchParams.get('query') || ''
  const currentSemId = searchParams.get('semId') || ''
  const currentStatus = searchParams.get('status') || ''
  const currentDegree = searchParams.get('degree') || ''
  const currentFacId = searchParams.get('facId') || ''
  const currentType = searchParams.get('type') || ''
  const typePref = searchParams.get('typePref') || ''
  const currentGeneral = searchParams.get('general') === 'true'
  const defaultSemId = activeSemester?.id != null ? String(activeSemester.id) : ''

  const defaultTypeId = React.useMemo(() => {
    const types = creditTypesResponse?.data ?? []
    const preferred = typePref === 'first' ? types[0] : types[1]
    return preferred != null ? String(preferred.id) : ''
  }, [creditTypesResponse?.data, typePref])

  const typeOptions = React.useMemo(
    () => [
      { value: '', label: t('common:any') },
      ...(creditTypesResponse?.data ?? []).map((ct) => ({ value: String(ct.id), label: ct.name })),
    ],
    [creditTypesResponse?.data, t]
  )

  React.useEffect(() => {
    const typeFromUrl = searchParams.get('type')
    if (typeFromUrl != null && typeFromUrl !== '') {
      return
    }
    const types = creditTypesResponse?.data ?? []
    const preferred = typePref === 'second' ? types[1] : types[0]
    if (preferred == null) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('type', String(preferred.id))
        if (next.get('typePref') != null) {
          next.delete('typePref')
        }
        return next
      },
      { replace: true }
    )
  }, [searchParams, creditTypesResponse?.data, setSearchParams, typePref])

  const statusOptions = React.useMemo(
    () => [
      { value: '', label: t('common:any') },
      ...(courseDefinitions.statuses ?? []).map((s) => ({ value: s.value, label: s.label })),
    ],
    [courseDefinitions.statuses, t]
  )
  const degreeOptions = React.useMemo(
    () => [
      { value: '', label: t('common:any') },
      ...(degrees ?? []).map((d) => ({ value: String(d.id), label: d.name })),
    ],
    [degrees, t]
  )
  const facultyOptions = React.useMemo(
    () => [{ value: '', label: t('common:any') }, ...(faculties ?? []).map((f) => ({ value: f.id, label: f.name }))],
    [faculties, t]
  )

  const isDirty =
    formik.values.query !== currentQuery ||
    formik.values.semId !== currentSemId ||
    formik.values.status !== currentStatus ||
    formik.values.degree !== currentDegree ||
    formik.values.facId !== currentFacId ||
    formik.values.type !== currentType ||
    formik.values.general !== currentGeneral
  const typeAtDefault = defaultTypeId !== '' ? currentType === defaultTypeId : !currentType
  const canCancel =
    !currentQuery &&
    currentSemId === defaultSemId &&
    !currentStatus &&
    !currentDegree &&
    !currentFacId &&
    typeAtDefault &&
    !currentGeneral

  const handleCancel = React.useCallback(() => {
    setSearchParams({
      query: '',
      semId: defaultSemId,
      status: '',
      degree: '',
      facId: '',
      type: defaultTypeId,
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
    })
    formik.resetForm({
      values: {
        query: '',
        semId: defaultSemId,
        status: '',
        degree: '',
        facId: '',
        type: defaultTypeId,
        general: false,
      },
    })
  }, [defaultSemId, defaultTypeId, setSearchParams, formik])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col justify-between">
      <FormError />

      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
          <TextInput
            type="text"
            label={t('common:enter_your_keyword')}
            name="query"
            placeholder={t('common:enter_your_keyword')}
          />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:mt-0 md:w-[49%]">
          <SelectInput
            label={t('program:faculty')}
            name="facId"
            options={facultyOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mr-0 mt-3 w-full min-w-[100px] md:!mt-3 md:mr-2 md:w-[49%]">
          <SelectInput
            label={t('person:degree')}
            name="degree"
            options={degreeOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:!mt-3 md:w-[49%]">
          <SelectInput
            label={t('common:status')}
            name="status"
            options={statusOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mr-0 mt-3 w-full min-w-[100px] md:!mt-3 md:mr-2 md:w-[49%]">
          <SelectInput label={t('common:type')} name="type" options={typeOptions} disabled={formik.isSubmitting} />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:!mt-3 md:w-[49%]">
          <SemesterSelect isFormInput />
        </div>

        <div className="mt-3 w-full min-w-[100px]">
          <Checkbox name="general" label={t('course:general_module_subjects')} />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty}
          type="submit"
          onClick={() => formik.submitForm()}
        >
          {t('common:search')}
        </Button>

        <Button
          variant="yellow"
          type="reset"
          disabled={formik.isSubmitting || canCancel}
          className="ml-2"
          onClick={handleCancel}
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

interface CoursesTableContentProps {
  readonly searchParams: URLSearchParams
}

function isPassiveCourse(course: Api.AdministratorCourse): boolean {
  return course.status.value === 2 || course.status.name === 'course_status_passive'
}

function CoursesTableContent({ searchParams }: CoursesTableContentProps): JSX.Element {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const locale = useLocale()

  const query = searchParams.get('query') || ''
  const semId = searchParams.get('semId') || ''
  const status = searchParams.get('status') || ''
  const degree = searchParams.get('degree') || ''
  const facId = searchParams.get('facId') || ''
  const typeParam = searchParams.get('type') || ''
  const general = searchParams.get('general') === 'true'
  const page = searchParams.get('page') || DEFAULT_PAGE
  const perPage = searchParams.get('perPage') || DEFAULT_PER_PAGE
  const sortBy = searchParams.get('sortBy') || 'id:asc'
  const { data: coursesData } = useApi({
    endpoint: Api.getAdministratorCourses,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page: parseInt(page, 10),
          perPage: parseInt(perPage, 10),
          ...(semId && { 'filters[semId]': parseInt(semId, 10) }),
          ...(status && { 'filters[status]': parseInt(status, 10) }),
          ...(degree && { 'filters[degree]': parseInt(degree, 10) }),
          ...(facId && { 'filters[facId]': parseInt(facId, 10) }),
          ...(typeParam && { 'filters[type]': parseInt(typeParam, 10) }),
          ...(query && { 'filters[query]': query }),
          ...(general && { 'filters[general]': '1' }),
          ...(sortBy && { sortBy: sortBy }),
        },
      }),
      [headers, page, perPage, semId, status, degree, facId, typeParam, query, general, sortBy]
    ),
  })

  if (coursesData == null || coursesData.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  return (
    <>
      <PaginationTop pagination={coursesData?.meta?.pagination} showTotalCount />

      <Table.Table className="mt-7">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="!pl-6">
              <div className="flex items-center gap-2">
                <span>{t('common:unique')}</span>
                <SortButtons sortField="id" />
              </div>
            </Table.Th>
            <Table.Th>
              <div className="flex items-center gap-2">
                <span>{t('common:code')}</span>
                <SortButtons sortField="code" />
              </div>
            </Table.Th>
            <Table.Th>
              <div className="flex items-center gap-2">
                <span>{t('common:name')}</span>
                <SortButtons sortField="name" />
              </div>
            </Table.Th>
            <Table.Th>
              <div className="flex items-center gap-2">
                <span>{t('program:program_code')}</span>
                <SortButtons sortField="programCode" />
              </div>
            </Table.Th>
            <Table.Th>
              <div className="flex items-center gap-2">
                <span>{t('course:credit')}</span>
                <SortButtons sortField="credits" />
              </div>
            </Table.Th>
            <Table.Th>
              <div className="flex items-center gap-2">
                <span>{t('common:step')}</span>
                <SortButtons sortField="degree" />
              </div>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {coursesData.data.map((course) => {
            const isPassive = isPassiveCourse(course)
            const passiveCellClassName = classNames({ '!text-primaryRed': isPassive })

            return (
              <Table.Tr key={course.id} className={classNames({ 'bg-seconderyRed/20': isPassive })}>
                <Table.Td className={classNames('whitespace-nowrap', passiveCellClassName)}>{course.id}</Table.Td>
                <Table.Td className={passiveCellClassName}>{course.code}</Table.Td>
                <Table.Td className={classNames(passiveCellClassName, 'text-primaryBlueLink hover:underline')}>
                  <Link
                    to={
                      semId !== ''
                        ? `/${locale}/administrator/administration/courses/${course.id}/edit?semId=${encodeURIComponent(semId)}`
                        : `/${locale}/administrator/administration/courses/${course.id}/edit`
                    }
                  >
                    {course.name}
                  </Link>
                </Table.Td>
                <Table.Td className={passiveCellClassName}>{course.programCode}</Table.Td>
                <Table.Td className={passiveCellClassName}>{course.credits.toFixed(2)}</Table.Td>
                <Table.Td className={passiveCellClassName}>{course.degree.name}</Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table.Table>

      <PaginationBottom pagination={coursesData?.meta?.pagination} />
    </>
  )
}
